<template>
  <div class="container">
    <!-- Filters -->
    <div class="filters-bar">
      <input type="date" class="filter-date-width" :value="dateToYYYYMMDD(selectedDate)" @input="selectedDate = $event.target.valueAsDate; loadJobs()" />
      <select v-model="selectedCompanyId" class="filter-clients-width">
        <option value="">All Clients</option>
        <option v-for="c in companies" :key="c.companyId" :value="c.companyId">{{ c.companyName }}</option>
      </select>
    </div>

    <!-- Toast Notification -->
    <div v-if="showToast" class="toast-container" :class="toastType">
      <div class="toast-content">
        <span class="toast-icon">{{ toastIcon }}</span>
        <span class="toast-message">{{ toastMessage }}</span>
        <span class="toast-close" @click="closeToast" style="pointer-events:auto; position:relative; z-index:9999;">×</span>
      </div>
    </div>

    <!-- Total -->
    <div class="total-banner" v-if="jobs.length">
      Total: {{ grandTotalHours }} ({{ jobs.length }} jobs) - {{ formatDayLabel(selectedDate) }}
    </div>

    <!-- Grid -->
    <div v-if="jobs.length > 0" class="schedule-grid">
      <div class="grid-header">
        <div class="time-col">Time</div>
        <div class="job-col">Jobs</div>
      </div>
      <div v-for="hour in filteredHours" :key="hour" class="grid-row">
        <div class="time-col">{{ pad(hour) }}:00</div>
        <div class="job-col">
          <div v-for="job in getJobsForHour(hour)" :key="job.jobId" class="job-block" :class="statusClass(job.statusId)">
            <span class="seq">{{ getSeq(job.jobId) }}</span>
            <div class="job-info">
              <div class="name">{{ job.securityOfficerInfo }}-{{job.mobileNumber}}</div>
              <div class="site">{{ job.siteInfo }}</div>
              <div class="meta">
                <span>{{ formatTime(job.startTime) }}-{{ formatTime(job.endTime) }}</span>
                <span>{{ duration(job) }}</span>
                <span>{{ job.clientInfo }}</span>
              </div>
            </div>
           
            <div class="actions">
              <button 
                class="btn-action btn-sia" 
                :class="{
                  'sia-verified': job.isSiaBadgeVerified === true,
                  'sia-unverified': job.isSiaBadgeVerified === false,
                  'sia-unknown': job.isSiaBadgeVerified === null || job.isSiaBadgeVerified === undefined
                }"
                :disabled="new Date(job.jobDate) < new Date(todayString) || new Date(job.jobDate) > new Date(todayString).getTime() + 24 * 60 * 60 * 1000 "
                @click.stop="new Date(job.jobDate) >= new Date(todayString) && handleSIABadgeVerification(job)" 
                :title="new Date(job.jobDate).getTime() === new Date(todayString).getTime() || new Date(job.jobDate).getTime() === new Date(todayString).getTime()+24 * 60 * 60 * 1000 ? ' SIA Badge Verification' : 'SIA verification only available for today and tomorrow\'s shifts'"
              >
                <i class="bi bi-shield-check"></i>
              </button>

              <button class="btn-action btn-calls" @click.stop="handleJobCalls(job)" title="Job Calls">
                <i class="bi bi-telephone"></i>
              </button>
              <button class="btn-action btn-chase" @click.stop="handleChaseupCalls(job)" title="Chaseup Calls">
                <i class="bi bi-clock-history"></i>
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- No Jobs Message -->
    <div v-else class="no-jobs-message">
      <p class="text-danger">No jobs found for {{ formatDayLabel(selectedDate) }}</p>
    </div>

    <!-- SIA License Details Modal -->
    <div v-if="showSiaModal" class="sia-modal-overlay" @click.self="showSiaModal = false">
      <div class="sia-modal">
        <div class="sia-modal-header">
          <h4 class="heading-center">SIA License Results</h4>
          <span class="sia-modal-close" @click="showSiaModal = false">×</span>
        </div>
        <div class="sia-modal-body">
          <div class="sia-row text-primary"><label>SIA Number</label><span>{{ siaResult.siaNumber }}</span></div>
          
          <div class="sia-row">
            <label>Name (In Our System)</label>
            <span :class="{'name-mismatch': siaResult.employeeName !== siaResult.licenseName}">
              {{ siaResult.employeeName }}
            </span>
          </div>
          <div class="sia-row">
            <label>Name (On License)</label>
            <span :class="{'name-mismatch': siaResult.employeeName !== siaResult.licenseName}">
              {{ siaResult.licenseName }}
            </span>
          </div>

          <div class="sia-row text-primary"><label>Role</label><span>{{ siaResult.role }}</span></div>
          <div class="sia-row text-primary"><label>License Type</label><span>{{ siaResult.licenseType }}</span></div>
          <div class="sia-row text-primary"><label>Expiry Date</label><span>{{ siaResult.expiryDate }}</span></div>
          <div class="sia-row text-primary">
            <label>Status</label>
            <span :class="siaResult.status === 'Active' ? 'sia-status-active' : 'sia-status-inactive'">
              {{ siaResult.status }}
            </span>
          </div>     
               
          <div class="sia-row"><label>Status Date</label><span>{{ siaResult.statusDate }}</span></div>
          <div class="sia-row"><label>Remarks</label><span>{{ siaResult.remarks }}</span></div>
        </div>
      </div>
    </div>
  </div>

  <!-- JobCallsModal Component -->
    <JobCallsModal 
      ref="jobCallsModal"
      :baseUrl="baseUrl"
    />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import JobCallsModal from "@/components/JobCallsModal.vue";
import lookupDataService from "@/services/LookupDataService";

// ============ TYPES ============
interface Company {
  companyId: number
  companyName: string
}

interface Job {
  jobId: number
  jobDate: string
  startTime: string | { Hours: number; Minutes: number }
  endTime: string | { Hours: number; Minutes: number }
  securityOfficerInfo: string
  securityOfficerId: number
  siteInfo: string
  clientInfo: string
  statusId: number
  siaBadgeNumber: string
  pinNumber?: string
  isSiaBadgeVerified?: boolean | null,
  mobileNumber?: string | null,
}

interface SiaResult {
  siaNumber: string
  employeePin: string
  employeeName: string
  licenseName: string
  role: string
  licenseType: string
  expiryDate: string
  status: string
  statusDate: string
  remarks: string
}

interface VerifyBadgeResult {
  status: string
  data?: {
    licenceNumber: string
    fullName: string
    role: string
    licenceSector: string
    expiryDate: string
    status: string
    statusDate: string
  }
  message?: string
}

// ============ PROPS ============
const props = defineProps<{
  baseUrl: string
}>()

// ============ DATA ============
const selectedDate = ref<Date>(new Date())
const jobs = ref<Job[]>([])
const companies = ref<Company[]>([])
const selectedCompanyId = ref<string>("")
const showToast = ref<boolean>(false)
const toastMessage = ref<string>("")
const toastType = ref<string>("info")
const toastTimeout = ref<number | null>(null)
const hours: number[] = [...Array(24).keys()]
const showSiaModal = ref<boolean>(false)
const todayString: string = new Date().toDateString()
const siaResult = ref<SiaResult>({
  siaNumber: '',
  employeePin: '',
  employeeName: '',
  licenseName: '',
  role: '',
  licenseType: '',
  expiryDate: '',
  status: '',
  statusDate: '',
  remarks: ''
});

const jobCallsModal = ref(null)

// ============ COMPUTED ============
const toastIcon = computed<string>(() => {
  const icons: Record<string, string> = { 
    success: "✅", 
    error: "❌", 
    warning: "⚠️", 
    info: "ℹ️" 
  }
  return icons[toastType.value] || "ℹ️"
})

const grandTotalHours = computed<string>(() => {
  let total = 0
  for (const j of jobs.value) {
    const start = toMinutes(j.startTime)
    const end = toMinutes(j.endTime)
    total += end - start
  }
  const h = Math.floor(total / 60)
  const m = total % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
})

const filteredHours = computed<number[]>(() => {
  const hoursWithJobs = new Set<number>()
  for (const j of jobs.value) {
    const h = Math.floor(toMinutes(j.startTime) / 60)
    hoursWithJobs.add(h)
  }
  const minHour = Math.min(...hoursWithJobs)
  const maxHour = Math.max(...hoursWithJobs)
  const result: number[] = []
  for (let h = minHour; h <= maxHour; h++) {
    if (hoursWithJobs.has(h)) result.push(h)
  }
  return result.length ? result : hours
})

// ============ METHODS ============
// UPDATED: Use cached data from localStorage instead of API call
function loadCompanies(): void {
  try {
    // Read from localStorage (cached by login)
    const cachedData = localStorage.getItem("lookupData");
    
    if (!cachedData) {
      console.warn("No cached data found. Please login again.");
      return;
    }
    
    const data = JSON.parse(cachedData);
    
    let companiesData = data.companies || [];
    if (companiesData.data && Array.isArray(companiesData.data)) {
      companiesData = companiesData.data;
    }
    if (companiesData.values && Array.isArray(companiesData.values)) {
      companiesData = companiesData.values;
    }
    
    companies.value = companiesData.map((c: any) => ({
      companyId: c.companyId || c.id || c.Id || 0,
      companyName: c.companyName || c.name || c.Name || 'Unknown'
    }));

    // Store callTypes if available
    if (data.callTypes) {
      localStorage.setItem("callTypes", JSON.stringify(data.callTypes));
    }
    
  } catch (e) {
    console.error("Failed to load companies from cache:", e);
  }
}

function closeToast(e: Event): void {
  e.stopPropagation()
  if (toastTimeout.value) {
    clearTimeout(toastTimeout.value)
    toastTimeout.value = null
  }
  showToast.value = false
  toastMessage.value = ""
}

function showNotification(message: string, type: string = "info"): void {
  if (toastTimeout.value) {
    clearTimeout(toastTimeout.value)
    toastTimeout.value = null
  }
  toastMessage.value = message
  toastType.value = type
  showToast.value = true
  toastTimeout.value = setTimeout(() => {
    showToast.value = false
  }, 5000) as unknown as number
}

async function loadJobs(): Promise<void> {
  const res = await fetch(props.baseUrl + "schedule/ScheduleDictionaryForDateRangePageWise", {
    method: "POST",
    headers: { 
      Authorization: `Bearer ${localStorage.token}`, 
      "Content-Type": "application/json" 
    },
    body: JSON.stringify({
      StartDate: selectedDate.value,
      EndDate: selectedDate.value,
      PageNumber: 0,
      RecordsPerPage: 1024 * 1024,
      CompanyId: parseInt(selectedCompanyId.value) || 0
    })
  })
  const data = await res.json()
  const key = getDateKey(selectedDate.value)
  jobs.value = data.scheduleSinglePageResult?.[key]?.scheduleViewModels || []
}

function getJobsForHour(hour: number): Job[] {
  return jobs.value.filter(j => {
    const m = toMinutes(j.startTime)
    return m >= hour * 60 && m < (hour + 1) * 60
  })
}

function toMinutes(t: string | { Hours: number; Minutes: number }): number {
  if (!t) return 0
  if (typeof t === 'string') {
    const parts = t.split(':')
    return parseInt(parts[0]) * 60 + parseInt(parts[1])
  }
  return t.Hours * 60 + (t.Minutes || 0)
}

function formatTime(t: string | { Hours: number; Minutes: number }): string {
  if (!t) return '--:--'
  if (typeof t === 'string') return t.substring(0, 5)
  return `${String(t.Hours).padStart(2, '0')}:${String(t.Minutes || 0).padStart(2, '0')}`
}

function duration(job: Job): string {
  const start = toMinutes(job.startTime)
  const end = toMinutes(job.endTime)
  const mins = end - start
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

function getDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T00:00:00`
}

function dateToYYYYMMDD(d: Date): string {
  return d.toISOString().split('T')[0]
}

function formatDayLabel(d: Date): string {
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })
}

function pad(n: number): string { 
  return String(n).padStart(2, '0') 
}

function statusClass(id: number): Record<string, boolean> {
  return {
    'status-completed': id == 2,
    'status-in-progress': id == 4,
    'status-pending': id == 3,
    'status-cancelled': id == 8
  }
}

function getSeq(id: number): number {
  return jobs.value.findIndex(j => j.jobId === id) + 1
}

async function verifyBadge(badge: string, jobId: number, securityOfficerId: number): Promise<VerifyBadgeResult> {
  const cleanBadge = badge.replace(/[\s-]/g, '')
  
  if (!cleanBadge || cleanBadge.length !== 16) {
    showNotification('Invalid badge number format', 'error')
    return { status: 'InvalidFormat' }
  }
  
  try {
    const res = await fetch(props.baseUrl + "schedule/VerifyBadge", {
      method: "POST",
      headers: { 
        Authorization: `Bearer ${localStorage.token}`, 
        "Content-Type": "application/json" 
      },
      body: JSON.stringify({
        BadgeNumber: cleanBadge,
        JobId: jobId,
        SecurityOfficerId: securityOfficerId
      })
    })

    const result = await res.json()
    return result
    
  } catch(e) {
    console.error(e)
    return { status: 'Error' }
  }
}

function handleSIABadgeVerification(job: Job): void {
  const badge = job.siaBadgeNumber
  const jobId = job.jobId
  const securityOfficerId = job.securityOfficerId
  const cleanBadge = badge ? badge.replace(/[\s-]/g, '') : ''
  
  verifyBadge(cleanBadge, jobId, securityOfficerId).then(result => {
    const index = jobs.value.findIndex(j => j.jobId === job.jobId)
    if (index !== -1) {
      jobs.value[index].isSiaBadgeVerified = (result.status === 'OK')
      jobs.value = [...jobs.value]
    }
    
    if (result.data) {
      siaResult.value = {
        siaNumber: result.data.licenceNumber || cleanBadge,
        employeePin: job.pinNumber || '',
        employeeName: job.securityOfficerInfo || 'Not Found',
        licenseName: result.data.fullName || '',
        role: result.data.role || '',
        licenseType: result.data.licenceSector || '',
        expiryDate: result.data.expiryDate || '',
        status: result.data.status || 'Unknown',
        statusDate: new Date().toLocaleDateString(),
        remarks: result.data.status === 'Active' ? 'OK' : 'CLOSE'
      }
    } else {
      siaResult.value = {
        siaNumber: cleanBadge,
        employeePin: job.pinNumber || '',
        employeeName: job.securityOfficerInfo || 'Not Found',
        licenseName: 'Not Found',
        role: 'N/A',
        licenseType: 'N/A',
        expiryDate: 'N/A',
        status: result.status || 'NotFound',
        statusDate: '',
        remarks: 'No licence found for this badge number'
      }
    }
    showSiaModal.value = true
    
    if (result.status === 'OK') {
      showNotification('SIA Badge ✅ Verified', 'success')
    } else if (result.status === 'InActive') {
      showNotification('SIA Badge ❌ Inactive or Expired', 'error')
    } else if (result.status === 'NotFound') {
      showNotification('SIA Badge ❌ Not Found', 'error')
    } else {
      showNotification(`SIA Badge ❌ ${result.status}`, 'error')
    }
  })
}

async function handleJobCalls(job: Job): Promise<any> {
  try {
    const res = await fetch(props.baseUrl + "JobCalls/CallsForJob", {
      method: "POST",
      headers: { 
        Authorization: `Bearer ${localStorage.token}`, 
        "Content-Type": "application/json" 
      },
      body: JSON.stringify({
        id: job.jobId,
      })
    })

    const result = await res.json()

    if (jobCallsModal.value) {
      jobCallsModal.value.openForEdit(result.jobCalls)
    }
    
  } catch(e) {
    console.error(e)
    return { status: 'Error' }
  }
}

function handleChaseupCalls(job: Job): void {
  showNotification(`Chaseup Calls for ${job.securityOfficerInfo || 'Job #' + job.jobId}`, "info")
}

// ============ LIFECYCLE ============
onMounted(() => {
  loadCompanies()
  loadJobs()
})

// ============ WATCHERS ============
watch(selectedCompanyId, () => {
  loadJobs()
})
</script>

<style scoped>
* { box-sizing: border-box; }
.filters-bar { display: flex; gap: 2px; padding: 8px; background: #f5f5f5; border-radius: 8px; margin-bottom: 12px; flex-wrap: wrap; }
.filters-bar input, .filters-bar select, .filters-bar button { padding: 8px 12px; border: 1px solid #ccc; border-radius: 4px; }
.total-banner { background: #667eea; color: white; padding: 10px 16px; border-radius: 8px; margin-bottom: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.filter-date-width{
  width:130px;
}
.filter-clients-width{
  width:215px;
}
.name-mismatch {
  background-color: #dc3545 !important;
  color: white !important;
  padding: 2px 8px;
  border-radius: 4px;
}

.schedule-grid { border: 1px solid #ddd; border-radius: 8px; overflow: hidden; }
.grid-header { display: grid; grid-template-columns: 70px 1fr; background: #f0f0f0; font-weight: bold; border-bottom: 2px solid #ccc; }
.grid-row { display: grid; grid-template-columns: 70px 1fr; border-bottom: 1px solid #eee; }
.grid-row:last-child { border-bottom: none; }
.time-col { padding: 8px; text-align: center; background: #fafafa; border-right: 1px solid #eee; }
.job-col { padding: 6px; }

.job-block { 
  display: flex; 
  align-items: center; 
  gap: 8px; 
  background: #2c3e50; 
  color: white; 
  padding: 4px 8px; 
  border-radius: 4px; 
  margin-bottom: 4px; 
}
.job-block:last-child { margin-bottom: 0; }
.job-block .seq { background: #ffc107; color: #333; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: bold; flex-shrink: 0; }
/* For in-progress status, ensure all child text uses dark color */
  .job-block.status-in-progress .job-info .name,
  .job-block.status-in-progress .job-info .site,
  .job-block.status-in-progress .job-info .meta,
  .job-block.status-in-progress .job-info .meta span:last-child {
    color: #333 !important;
  }
.job-info { flex: 1; min-width: 0; }
.job-info .name { 
  font-weight: bold; 
  font-size: 11px; 
  white-space: nowrap; 
  overflow: hidden; 
  text-overflow: ellipsis; 
}
.job-info .site { 
  color: #ffd966; 
  white-space: nowrap; 
  overflow: hidden; 
  text-overflow: ellipsis; 
}
.job-info .meta { 
  display: flex; 
  gap: 8px; 
  color: #ccc; 
  flex-wrap: wrap; 
}
.job-info .meta span:last-child { color: white; }

.actions { display: flex; gap: 4px; flex-shrink: 0; }

.actions .btn-action { border: none; border-radius: 4px; padding: 3px 6px; cursor: pointer; font-size: 13px; display: flex; align-items: center; justify-content: center; width: 26px; height: 26px; background: white; }
.actions .btn-action i { font-size: 14px; }
.actions .btn-action:hover { opacity: 0.8; transform: scale(1.05); }

.actions .btn-sia { color: #0d6efd; background: white; border: 1px solid #0d6efd; }
.actions .btn-sia:hover { background: #0d6efd; color: white; }
.actions .btn-calls { color: #198754; background: white; border: 1px solid #198754; }
.actions .btn-calls:hover { background: #198754; color: white; }
.actions .btn-chase { color: #ffc107; background: white; border: 1px solid #ffc107; }
.actions .btn-chase:hover { background: #ffc107; color: #333; }

.status-completed { background: #28a745; color: white; }
.status-in-progress { background: #ffc107; color: #333; }
.status-in-progress .site { color: #856404 !important; } /* Darker amber for better contrast */
.status-pending { background: #dc3545; color: white; }
.status-cancelled { background: #6c757d; opacity: 0.7; color: #e0e0e0; }

.toast-container {
  position: fixed; top: 20px; right: 20px; z-index: 999; max-width: 400px; min-width: 280px;
  animation: slideIn 0.3s ease;
}
.toast-content {
  background: white; border-radius: 8px; padding: 14px 18px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  display: flex; align-items: center; gap: 10px; border-left: 4px solid #17a2b8;
}
.toast-container.success .toast-content { border-left-color: #28a745; }
.toast-container.error .toast-content { border-left-color: #dc3545; }
.toast-container.warning .toast-content { border-left-color: #ffc107; }
.toast-container.info .toast-content { border-left-color: #17a2b8; }
.toast-icon { font-size: 20px; flex-shrink: 0; }
.toast-message { flex: 1; font-size: 13px; color: #333; white-space: pre-line; }
.toast-close { font-size: 24px; cursor: pointer; color: #999; padding: 0 4px; line-height: 1; user-select: none; font-weight: 300; }
.toast-close:hover { color: #333; }
@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }

.text-danger{
    color: #ff0000 !important;
    font-size: 20px !important;
  }
@media (max-width: 768px) {
  .grid-header, .grid-row { grid-template-columns: 50px 1fr; }
  .time-col { padding: 6px; font-size: 11px; }
  .job-block { flex-wrap: wrap; padding: 6px; }
  .job-info .name { font-size: 10px; }
  .job-info .site { font-size: 9px; }
  .job-info .meta { font-size: 8px; gap: 4px; }
  .actions { margin-left: auto; }
  .actions .btn-action { width: 22px; height: 22px; font-size: 11px; }
  .actions .btn-action i { font-size: 12px; }
  .filter-date-width, .filter-clients-width { width: 100%; }
  .filters-bar { flex-direction: column; }
  .total-banner { font-size: 12px; white-space: normal; }
}

/* SIA Verified - Green Background */
.btn-sia.sia-verified {
  color: white !important;
  background: #28a745 !important;
  border: 1px solid #28a745 !important;
}
.btn-sia.sia-verified:hover {
  background: #1e7e34 !important;
  color: white !important;
}

/* SIA Unverified - Red Background */
.btn-sia.sia-unverified {
  color: white !important;
  background: #dc3545 !important;
  border: 1px solid #dc3545 !important;
}
.btn-sia.sia-unverified:hover {
  background: #a71d2a !important;
  color: white !important;
}

/* SIA Unknown - Blue Background (default) */
.btn-sia.sia-unknown {
  color: white !important;
  background: #0d6efd !important;
  border: 1px solid #0d6efd !important;
}
.btn-sia.sia-unknown:hover {
  background: #0a58ca !important;
  color: white !important;
}

/* SIA Modal Styles */
.sia-modal-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
}
.sia-modal {
  background: white;
  border-radius: 12px;
  padding: 24px 30px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0,0,0,0.3);
}
.sia-modal-header {
  display: flex; justify-content: space-between; align-items: center;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 12px;
  margin-bottom: 16px;
}
.sia-modal-header h4 { margin: 0; color: #2c3e50; font-size: 16px; }
.sia-modal-close { font-size: 28px; cursor: pointer; color: #999; line-height: 1; }
.sia-modal-close:hover { color: #333; }
.sia-row { display: flex; padding: 8px 0; border-bottom: 1px solid #f5f5f5; text-align: left;}
.sia-row label { font-weight: 600; width: 180px; flex-shrink: 0; color: #555; font-size: 13px;}
.sia-row span {
  flex: 1;
  color: #333;
  font-size: 13px;
  word-break: break-word;
  background-color: #f5f5f5;
  padding: 4px 10px;
  border-radius: 4px;
}
  .sia-status-active { 
    color: #28a745; 
    font-weight: 700; 
    background-color: #28a745 !important; 
    color: white !important;
    padding: 2px 8px;
    border-radius: 4px;
  }
  .sia-status-inactive { 
    color: #dc3545; 
    font-weight: 700; 
    background-color: #dc3545 !important; 
    color: white !important;
    padding: 2px 8px;
    border-radius: 4px;
  }

@media (max-width: 600px) {
  .sia-modal { padding: 16px; }
 .sia-row {
  display: flex;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
 }
  .sia-row label {
    font-weight: 600;
    width: 180px;
    flex-shrink: 0;
    color: #555;
    font-size: 13px;
  }
  .sia-row span {
    flex: 1;
    color: #333;
    font-size: 13px;
    word-break: break-word;
    background-color: #f5f5f5;
    padding: 4px 10px;
    border-radius: 4px;
  }

  .no-jobs-message {
    text-align: center;
    padding: 40px 20px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }
  .no-jobs-message p {
    font-size: 16px;
    color: #6c757d;
    margin: 0;
    color: #ff0000 !important;
  }

  .heading-center {
    text-align: center !important;
    color: #ff0000 !important;
}

  
}
</style>